import { Add } from "@mui/icons-material";
import { Button } from "@mui/material";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { DwellTimeUpticksQuery, Scalars } from "../../../graphql/generated";
import { TableField } from "../../common/LynksTable/LynksTable";
import SmartLynksTable, {
  DeleteMutation,
  QueryFn,
} from "../../common/SmartLynksTable/SmartLynksTable";
import DwellTimeUptickDialogForm from "../BusinessEntityForm/DwellTimeUptickForm";
import { formatInTimeZone } from "date-fns-tz";
import { testIds } from "./DwellTimeUpticksList.testIds";

const fallbackTimezone = "America/Los_Angeles";

type DwellTimeUptickListItem =
  DwellTimeUpticksQuery["dwellTimeUpticks"]["data"][0];

export type DwellTimeUpticksListProps = {
  query: QueryFn<DwellTimeUptickListItem, "dwellTimeUpticks">;
  deleteMutation: DeleteMutation;
  onRefresh: () => void;
  businessEntityId: Scalars["ObjectId"];
  onSave: (dwellTimeUptick: any, uptickId?: string) => Promise<void>;
  businessEntityTimezone?: string | null;
};

export default function DwellTimeUpticksList({
  query,
  deleteMutation,
  onRefresh,
  businessEntityId,
  onSave,
  businessEntityTimezone,
}: DwellTimeUpticksListProps) {
  const { t } = useTranslation(["business", "common"]);
  const [isOpenDialog, setIsOpenDialog] = useState(false);
  const [editingUptick, setEditingUptick] =
    useState<DwellTimeUptickListItem | null>(null);
  const timezone = businessEntityTimezone || fallbackTimezone;

  const fields: TableField<DwellTimeUptickListItem>[] = [
    {
      key: "startDate",
      label: t("startDate", "Start Date & Time"),
      type: "string",
      value: (dwellTimeUptick) =>
        dwellTimeUptick.startDate
          ? `${formatInTimeZone(
              new Date(dwellTimeUptick.startDate),
              timezone,
              "MMM d, yyyy h:mm a zzz",
            )}`
          : "N/A",
    },
    {
      key: "endDate",
      label: t("endDate", "End Date & Time"),
      type: "string",
      value: (dwellTimeUptick) =>
        dwellTimeUptick.endDate
          ? `${formatInTimeZone(
              new Date(dwellTimeUptick.endDate),
              timezone,
              "MMM d, yyyy h:mm a zzz",
            )}`
          : "N/A",
    },
    {
      key: "addedTimeInMinutes",
      label: t("addedTimeInMinutes", "Added Time in Minutes"),
      type: "number",
      value: (dwellTimeUptick) => dwellTimeUptick.addedTimeInMinutes,
    },
  ];

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
      }}
    >
      <SmartLynksTable<DwellTimeUptickListItem, "dwellTimeUpticks">
        query={query}
        dataKey="dwellTimeUpticks"
        fields={fields}
        id="business-dwell-time-upticks-list"
        showGroups
        filterMode="server"
        sortingMode="server"
        paginationMode="server"
        deleteMutation={deleteMutation}
        onRecordClick={(uptick) => {
          setEditingUptick(uptick);
          setIsOpenDialog(true);
        }}
        renderHeaderActions={() => (
          <Button data-testid={testIds.dwellTimeUpticksListCreate}
            variant="contained"
            color="primary"
            startIcon={<Add />}
            onClick={() => {
              setEditingUptick(null);
              setIsOpenDialog(true);
            }}
          >
            {t("business:addDwellTimeUptick", "Add Dwell Time Uptick")}
          </Button>
        )}
      />
      <DwellTimeUptickDialogForm
        open={isOpenDialog}
        onClose={() => {
          setIsOpenDialog(false);
          setEditingUptick(null);
        }}
        onSave={async (dwellTimeUptick) => {
          await onSave(dwellTimeUptick, editingUptick?._id);
          setIsOpenDialog(false);
          setEditingUptick(null);
          onRefresh();
        }}
        businessEntityId={businessEntityId}
        businessEntityTimezone={businessEntityTimezone}
        editingUptick={editingUptick}
      />
    </div>
  );
}
